import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { buildAboutLd } from "@/lib/page-schemas";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About DrHint — Our Mission & How We Calculate Prices" },
      { name: "description", content: "Learn about DrHint, our independent gold price calculation method, and our editorial policy." },
      { property: "og:title", content: "About DrHint" },
      { property: "og:description", content: "Independent gold price tracking for Pakistan and India." },
      { property: "og:url", content: "https://drhint.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://drhint.com/about" }],
    scripts: buildAboutLd({
      url: "https://drhint.com/about",
      name: "About DrHint",
      description: "Independent gold price tracking and utility-bill tools for Pakistan, India, UAE and Saudi Arabia.",
      trail: [
        { name: "Home", item: "https://drhint.com/" },
        { name: "About", item: "https://drhint.com/about" },
      ],
    }),
  }),
  component: () => (
    <Layout>
      <article className="mx-auto max-w-3xl px-4 py-10 prose prose-sm">
        <h1 className="text-3xl font-bold">About DrHint</h1>
        <p className="mt-4 text-muted-foreground">
          DrHint is an independent gold price tracker covering the top 10 cities in
          Pakistan and India. Our goal is simple: give buyers, sellers and curious readers a clear,
          honest view of what 24K and 22K gold costs today — without the inflated headlines or
          confusing units.
        </p>

        <h2 className="mt-8 text-xl font-bold">How we calculate prices</h2>
        <p className="mt-3 text-muted-foreground">
          Every rate on this site starts from the live international spot price of gold, quoted in
          US dollars per troy ounce. We multiply by the current USD-to-PKR or USD-to-INR exchange
          rate, convert to per-gram, per-tola and per-10-gram units, and apply the standard
          country-level premium that covers import duty, taxes and dealer margin. A small
          city-level adjustment accounts for transport distance and local association costs.
        </p>
        <p className="mt-3 text-muted-foreground">
          The full step-by-step method is published in our blog post,{" "}
          <Link to="/blog/$slug" params={{ slug: "how-gold-prices-are-calculated" }} className="text-primary underline">
            How Gold Prices Are Calculated
          </Link>.
        </p>

        <h2 className="mt-8 text-xl font-bold">How often rates update</h2>
        <p className="mt-3 text-muted-foreground">
          Prices refresh every hour. The last-updated timestamp is shown next to each city grid.
          We deliberately avoid second-by-second updates because they create noise without helping
          retail buyers make better decisions.
        </p>

        <h2 className="mt-8 text-xl font-bold">Editorial independence</h2>
        <p className="mt-3 text-muted-foreground">
          We are not affiliated with any jeweller, bullion dealer or gold trading platform. Our
          articles are written for ordinary buyers, not to push any specific product. When we
          mention a category of seller, it is general guidance — not a recommendation.
        </p>

        <h2 className="mt-8 text-xl font-bold">Get in touch</h2>
        <p className="mt-3 text-muted-foreground">
          Questions, corrections, or a city you'd like us to add?{" "}
          <Link to="/contact" className="text-primary underline">Visit our contact page</Link>.
        </p>
      </article>
    </Layout>
  ),
});